/**
 * Servicio de Categorías que usa la nueva API backend
 * Siguiendo el mismo patrón que otros servicios
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';

/**
 * Obtiene todas las categorías por tipo
 * @param {string} tipo - Tipo de categoría ('especialidad_mecanico', 'categoria_servicio', 'categoria_repuesto', 'tipo_vehiculo')
 * @returns {Promise<Array>} Lista de categorías
 */
export const getCategoriasByTipo = async (tipo) => {
  try {
    //console.log(`🏷️ [CategoriasService] Obteniendo categorías por tipo: ${tipo}`);
    
    const response = await apiClient.get(API_ENDPOINTS.CATEGORIAS.BY_TIPO(tipo));
    
    if (response.data) {
      const categoriasData = response.data.data || response.data.categorias || response.data;
      //console.log(`✅ [CategoriasService] ${categoriasData.length} categorías encontradas para tipo ${tipo}`);
      return categoriasData;
    }
    
    return [];
  } catch (error) {
    console.error(`❌ [CategoriasService] Error al obtener categorías por tipo ${tipo}:`, error);
    throw error;
  }
};

/**
 * Obtiene todas las categorías activas
 * @returns {Promise<Array>} Lista de todas las categorías
 */
export const getAllCategorias = async () => {
  try {
    //console.log('🏷️ [CategoriasService] Obteniendo todas las categorías...');
    
    const response = await apiClient.get(API_ENDPOINTS.CATEGORIAS?.BASE || '/categorias');
    
    if (response.data) {
      const categoriasData = response.data.data || response.data.categorias || response.data;
      //console.log(`✅ [CategoriasService] ${categoriasData.length} categorías encontradas`);
      return categoriasData;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [CategoriasService] Error al obtener todas las categorías:', error);
    throw error;
  }
};

/**
 * Obtiene una categoría por ID
 * @param {number} id - ID de la categoría
 * @returns {Promise<Object>} Categoría encontrada
 */
export const getCategoriaById = async (id) => {
  try {
    //console.log(`🔍 [CategoriasService] Buscando categoría por ID: ${id}`);
    
    const response = await apiClient.get(API_ENDPOINTS.CATEGORIAS?.BY_ID ? 
      API_ENDPOINTS.CATEGORIAS.BY_ID(id) : 
      `${API_ENDPOINTS.CATEGORIAS?.BASE || '/categorias'}/${id}`
    );
    
    if (response.data) {
      const categoriaData = response.data.data || response.data.categoria || response.data;
      //console.log('✅ [CategoriasService] Categoría encontrada por ID');
      return categoriaData;
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log(`ℹ️ [CategoriasService] Categoría con ID ${id} no encontrada`);
      return null;
    }
    console.error(`❌ [CategoriasService] Error al buscar categoría por ID ${id}:`, error);
    throw error;
  }
};

/**
 * Crea una nueva categoría
 * @param {Object} categoria - Datos de la categoría
 * @returns {Promise<Object>} Categoría creada
 */
export const createCategoria = async (categoria) => {
  try {
    //console.log('🏷️ [CategoriasService] Creando nueva categoría...');
    
    const response = await apiClient.post(API_ENDPOINTS.CATEGORIAS?.BASE || '/categorias', categoria);
    
    if (response.data) {
      const categoriaCreada = response.data.data || response.data.categoria || response.data;
      //console.log('✅ [CategoriasService] Categoría creada exitosamente');
      return categoriaCreada;
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error('❌ [CategoriasService] Error al crear categoría:', error);
    throw error;
  }
};

/**
 * Actualiza una categoría existente
 * @param {number} id - ID de la categoría
 * @param {Object} updates - Datos a actualizar
 * @returns {Promise<Object>} Categoría actualizada
 */
export const updateCategoria = async (id, updates) => {
  try {
    //console.log(`🔄 [CategoriasService] Actualizando categoría ${id}...`);
    
    const response = await apiClient.put(API_ENDPOINTS.CATEGORIAS?.BY_ID ? 
      API_ENDPOINTS.CATEGORIAS.BY_ID(id) : 
      `${API_ENDPOINTS.CATEGORIAS?.BASE || '/categorias'}/${id}`, 
      updates
    );
    
    if (response.data) {
      const categoriaActualizada = response.data.data || response.data.categoria || response.data;
      //console.log('✅ [CategoriasService] Categoría actualizada exitosamente');
      return categoriaActualizada;
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error(`❌ [CategoriasService] Error al actualizar categoría ${id}:`, error);
    throw error;
  }
};

/**
 * Elimina una categoría (soft delete)
 * @param {number} id - ID de la categoría
 * @returns {Promise<string>} ID de la categoría eliminada
 */
export const deleteCategoria = async (id) => {
  try {
    //console.log(`🗑️ [CategoriasService] Eliminando categoría ${id}...`);
    
    const response = await apiClient.delete(API_ENDPOINTS.CATEGORIAS?.BY_ID ? 
      API_ENDPOINTS.CATEGORIAS.BY_ID(id) : 
      `${API_ENDPOINTS.CATEGORIAS?.BASE || '/categorias'}/${id}`
    );
    
    if (response.data) {
      //console.log('✅ [CategoriasService] Categoría eliminada exitosamente');
      return id.toString();
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error(`❌ [CategoriasService] Error al eliminar categoría ${id}:`, error);
    throw error;
  }
};

/**
 * Obtiene categorías agrupadas por tipo
 * @returns {Promise<Object>} Categorías agrupadas por tipo
 */
export const getCategoriesGroupedByType = async () => {
  try {
    //console.log('📊 [CategoriasService] Obteniendo categorías agrupadas por tipo...');
    
    const response = await apiClient.get(API_ENDPOINTS.CATEGORIAS.GROUPED);
    
    if (response.data) {
      const grouped = response.data.data || response.data;
      //console.log('✅ [CategoriasService] Categorías agrupadas exitosamente');
      return grouped;
    }
    
    return {
      especialidad_mecanico: [],
      categoria_servicio: [],
      categoria_repuesto: [],
      tipo_vehiculo: []
    };
  } catch (error) {
    console.error('❌ [CategoriasService] Error al obtener categorías agrupadas:', error);
    // Fallback: usar método manual si el endpoint no funciona
    try {
      const categorias = await getAllCategorias();
      
      const grouped = {
        especialidad_mecanico: [],
        categoria_servicio: [],
        categoria_repuesto: [],
        tipo_vehiculo: []
      };

      categorias.forEach(categoria => {
        if (grouped[categoria.tipo]) {
          grouped[categoria.tipo].push(categoria);
        }
      });

      return grouped;
    } catch (fallbackError) {
      console.error('❌ [CategoriasService] Error en fallback:', fallbackError);
      throw error;
    }
  }
};

/**
 * Alias para compatibilidad con el código existente
 */
export const categoriasApi = {
  getCategoriasByTipo,
  getAllCategorias,
  getCategoriaById,
  createCategoria,
  updateCategoria,
  deleteCategoria,
  getCategoriesGroupedByType
};

// Exportar como default para compatibilidad
export default categoriasApi;